/**
 * Move an array item from one position to another
 * @param {Array} array - The array to modify
 * @param {number} from - The source index
 * @param {number} to - The destination index
 * @returns {Array} A new array with the item moved
 */
export const arrayMove = (array, from, to) => {
  const newArray = [...array];
  const item = newArray.splice(from, 1)[0];
  newArray.splice(to, 0, item);
  return newArray;
};

/**
 * Reorder an array based on drag and drop event
 * @param {Array} items - The array to reorder
 * @param {string} activeId - The ID of the active (dragged) item
 * @param {string} overId - The ID of the item being dragged over
 * @returns {Array} A new array with items reordered
 */
export const reorderItems = (items, activeId, overId) => {
  if (activeId === overId) {
    return items;
  }

  const oldIndex = items.findIndex((item) => item.id === activeId);
  const newIndex = items.findIndex((item) => item.id === overId);

  return arrayMove(items, oldIndex, newIndex);
};

/**
 * Find the container (parent) that contains a specific item
 * @param {Array} containers - Array of containers to search
 * @param {string} itemId - The ID of the item to find
 * @returns {Object|null} The container that contains the item, or null if not found
 */
export const findContainer = (containers, itemId) => {
  for (const container of containers) {
    if (container.id === itemId) {
      return container;
    }
    if (container.items && container.items.find((item) => item.id === itemId)) {
      return container;
    }
  }
  return null;
};

/**
 * Check if an item is being dragged over a valid drop target
 * @param {Object} dragEvent - The drag event object
 * @param {Array} validTargets - Array of valid drop target IDs
 * @returns {boolean} True if the item is over a valid target
 */
export const isValidDropTarget = (dragEvent, validTargets) => {
  if (!dragEvent.over) return false;
  return validTargets.includes(dragEvent.over.id);
};

/**
 * Get the drop position indicator based on drag event
 * @param {Object} dragEvent - The drag event object
 * @returns {string} 'above' | 'below' | 'inside' | null
 */
export const getDropPosition = (dragEvent) => {
  if (!dragEvent.over) return null;

  const { delta } = dragEvent;
  if (!delta) return null;

  // Simple logic - can be enhanced based on needs
  if (delta.y < -10) return "above";
  if (delta.y > 10) return "below";
  return "inside";
};
